import * as React from "react"
import {
    Dropdown,
    DropdownItemProps,
    DropdownProps,
    Header,
    Icon,
    Label,
    LabelProps,
    Message,
    Popup,
} from "semantic-ui-react"
import ButtonLock from "../buttons/com_button-lock"
import {isError, genericConvert, getlangFromCache} from "../../../utils"
import {IFormFieldProps, IOnChangeProps} from "."
import ButtonChange from "../buttons/com_button-change"
import {FieldType, permanentFormIds} from "../../../models"
import {
    ILocalizedString,
    Multilang,
    MultilangString,
} from "../../../models/product/multilang-string"
import {
    checkDropdownValueIntegrity,
    PreviousValueMessage,
} from "./form-field-tools"
import {stringify} from "../../../utils/stringifyier"
import {
    DropdownOptionRawValue,
    IDropdownOption,
} from "../../product-form/_reducers/_utils/product-form-types"
import {SemanticICONS} from "semantic-ui-react/dist/commonjs/generic"
import {deleteDoublons} from "../../../utils/doublon-tools";
import {EMPTY_ARRAY} from "../../../utils/memo"

export interface IComboBoxState {
    loading: boolean
}

const ComboBox = (props: IFormFieldProps<DropdownOptionRawValue>) => {
    const {
        data,
        label = "",
        disabled = false,
        onLockClick,
        dropdownProps,
        formId,
        onModifyClick,
        isNewProduct,
        onChange,
        oldValue,
        type,
    } = props
    const {errorText, code, lock, isRequired, fieldType} = data

    const sourceOptions: IDropdownOption[] = dropdownProps?.options || EMPTY_ARRAY
    const isMultiple = dropdownProps?.multiple || false
    const allowAdditionsIfNoOptions =
        sourceOptions.length <= 0 && (dropdownProps?.allowAdditionsIfNoOptions || false)
    const isObject = dropdownProps?.isObject || false
    const readOnlyData = dropdownProps?.readOnlyData || EMPTY_ARRAY
    const [loading, setLoading] = React.useState(false)
    const [initialized, setInitialized] = React.useState(false)
    const [addedOptions, setAddedOptions] = React.useState<any[]>([])

    React.useEffect(() => {
        setLoading(!dropdownProps)
    }, [!dropdownProps, sourceOptions])

    // Filtering and sorting (localeCompare) only depend on the source list, which keeps its reference while it is
    // not reloaded (FE-PERF-F07). The placeholder options are tiny: their content is the dependency.
    const placeholderKey = stringify(dropdownProps?.customPlaceholderOption)
    const options = React.useMemo(() => {
        let result = sourceOptions
            .filter((val: IDropdownOption) =>
                ["-", ""].every((el) => el !== val.text.trim())
            )
            .sort((a: IDropdownOption, b: IDropdownOption) =>
                a.text.localeCompare(b.text)
            )
        if (!isMultiple) {
            if (dropdownProps?.customPlaceholderOption) {
                if (Array.isArray(dropdownProps?.customPlaceholderOption)) {
                    result = [...dropdownProps?.customPlaceholderOption, ...result]
                } else {
                    result = [dropdownProps?.customPlaceholderOption, ...result]
                }
            } else {
                result = [
                    {
                        key: "-",
                        text: "-",
                        value: "-1",
                    },
                    ...result,
                ]
            }
        }
        return result
    }, [sourceOptions, isMultiple, placeholderKey])

    React.useEffect(() => {
        if (
            options.length <= 0 ||
            initialized ||
            (fieldType === FieldType.Standard &&
                data.value === data.regenerateInfos.defaultValue)
        ) {
            return
        }
        const checkedValue = checkDropdownValueIntegrity(data, options, isMultiple)
        if (JSON.stringify(data.value) !== JSON.stringify(checkedValue)) {
            handleChange({
                code: data.code,
                fieldValue: checkedValue as string | MultilangString,
                lock: data.lock,
                fieldType: data.fieldType,
                overrideDefaultValue: true,
            })
        }
        setInitialized(true)
    }, [data.code, options, data.value])

    const handleChange = (props: IOnChangeProps) => {
        if (
            readOnlyData &&
            readOnlyData.length > 0 &&
            Array.isArray(props.fieldValue)
        ) {
            onChange({
                ...props,
                fieldValue: (props.fieldValue as string[]).filter(
                    (x) => !readOnlyData.find((r) => r.value === x)
                ),
            })
        } else {
            onChange(props)
        }
    }

    const handleSelectChange = (event: any, d: any) => {
        const newValue = isObject ? JSON.parse(d.value) : d.value
        if(Array.isArray(newValue)) {
            const index = (newValue as Array<any>).findIndex((x) => x === "");
            if(index !== -1) {
                newValue.splice(index, 1);
            }
        }
        handleChange({
            code: data.code,
            fieldValue: newValue,
            lock: data.lock,
            fieldType: data.fieldType,
            forceAcceptEmptyArray: true,
            allowDefaultValue: true,
        })
    }

    const onClick = () => setLoading(false)

    const {
        icon = "",
        style = {globalDiv: {}, div: {}, input: {}, label: {}},
        labelClassName = "",
        minCaracters = 1,
        lazyload = true,
        className = "",
    } = dropdownProps || {
        icon: "",
        className: "",
        labelClassName: "",
        style: {globalDiv: {}, div: {}, input: {}, label: {}},
        minCaracters: 1,
        layload: true,
    }

    let finalOptions: DropdownItemProps[] = React.useMemo(() => {
        const opt = isObject
            ? options.map((o) => ({
                ...o,
                value: JSON.stringify(o.value),
            }))
            : (options as DropdownItemProps[])

        const readOnly =
            readOnlyData
                ?.filter((x) => !options.find((o) => o.value === x.value))
                .map((t) => ({value: t.value, text: t.value})) || []

        const fromData = Array.isArray(data.value)
            ? Multilang.String.IsMultilangString(data.value)
                ? [Multilang.String.GetValue(data.value as ILocalizedString[], getlangFromCache())]
                : (data.value as string[])
                    .filter((x) => !options.find((o) => o.value === x))
                    .map((x) => ({value: x, text: x}))
            : []

        return [...opt, ...addedOptions, ...readOnly, ...fromData]
    }, [addedOptions, isObject, options, readOnlyData, data.value])

    let finalValue:
        | string
        | number
        | boolean
        | Array<string | number | boolean>
        | undefined = React.useMemo(() => {
        let val: any
        if (isObject) {
            val = JSON.stringify(data.value)
        } else {
            val = data.value as string
        }
        if (readOnlyData && Array.isArray(val)) {
            val = deleteDoublons([
                ...(Multilang.String.IsMultilangString(data.value)
                    ? [Multilang.String.GetValue(data.value as ILocalizedString[], getlangFromCache())]
                    : val),
                ...readOnlyData
                    .filter((x) => !val.find((v: string) => v === x.value))
                    .map((x) => x.value),
            ])
        } else if (isMultiple && !Array.isArray(val)) {
            val = [val].filter((x) => x !== "-1" && x !== "-" && x !== "")
        }
        return val
    }, [data.value, isObject, readOnlyData])

    // console.log({ readOnlyData, finalValue })

    let oldValueText = Array.isArray(oldValue?.value)
        ? oldValue?.value.join(" - ")
        : oldValue?.isEmpty
            ? oldValue?.value
            : finalOptions.find(
                (o) =>
                    o.value ===
                    (isObject ? JSON.stringify(oldValue?.value) : oldValue?.value)
            )?.text

    if (!oldValueText) oldValueText = oldValue?.value.toString()
    if (oldValueText === "-1") oldValueText = "N/A"
    if (oldValueText === "") oldValueText = "N/A"

    const isDeclinable = data.isDeclinable

    const handleAddition = (
        event: React.SyntheticEvent<HTMLElement, Event>,
        {value}: DropdownProps
    ) => {
        setAddedOptions([...addedOptions, {text: value, value}])
        // console.log([...addedOptions, { text: value, value }])
    }

    const renderLabel = (
        item: DropdownItemProps,
        index: number,
        defaultLabelProps: LabelProps
    ) => {
        // console.log(item)
        const found = readOnlyData.find((x) => x.value === item.value)
        if (found) {
            return {
                content: `${item.text}`,
                // content: `${found.label} - ${item.text}`,
                // icon: "check",
                className: found.className,
            }
        }
        return {
            content: item.text,
        }
    }

    const handleLabelClick = (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        item: LabelProps
    ) => {
        event.preventDefault()
        event.stopPropagation()
        const found = readOnlyData.find((x) => x.value === item.value)
        if (found) {
            found.onClick()
        }
    }

    // console.log({
    //   data: data.label,
    //   addedOptions,
    //   finalOptions,
    //   finalValue,
    //   isMultiple,
    //   isDeclinable,
    // })

    const noResultMessage = allowAdditionsIfNoOptions
        ? "Entrez une valeur"
        : "Aucune valeur trouvée"

    const genericSort = (
        a: string | ILocalizedString,
        b: string | ILocalizedString
    ) => {
        if (
            typeof a === "string" ||
            typeof a === "number" ||
            typeof b === "string" ||
            typeof b === "number"
        ) {
            return (a + "").localeCompare(b + "")
        }
        if (a.lang && a.value && b.lang && b.value) {
            return `${a.lang} - ${a.value}`.localeCompare(`${b.lang} - ${b.value}`)
        }
        return 0
    }
    
    const finalOldValue = Multilang.String.IsMultilangString(oldValue?.value)
                ? Multilang.String.GetValue(oldValue?.value as ILocalizedString[], getlangFromCache())
                : oldValue?.value

    let showOldValue =
        oldValue &&
        finalValue &&
        finalValue !== "-1" &&
        finalValue !== -1 &&
        (isObject ? JSON.stringify(finalOldValue) : finalOldValue) !== finalValue

    if (showOldValue && isObject)
        showOldValue = JSON.stringify(finalOldValue) !== finalValue

    if (showOldValue && Array.isArray(finalValue) && finalValue.length === 1)
        showOldValue = finalValue[0] !== finalOldValue

    if (
        showOldValue &&
        Array.isArray(finalOldValue) &&
        Array.isArray(finalValue)
    )
        showOldValue =
            JSON.stringify(finalOldValue.sort(genericSort)) !==
            JSON.stringify((finalValue as string[]).sort(genericSort))

    return (
        <div className="lock-div" style={style.globalDiv}>
            {showOldValue ? (
                <PreviousValueMessage
                    label={oldValue?.formattedDate || "DATE"}
                    content={
                        <>
                            {oldValueText || finalOldValue?.toString()}
                            {icon && <Icon name={icon as SemanticICONS}/>}
                        </>
                    }
                />
            ) : (
                ""
            )}
            {isError(errorText) ? (
                <Message className="error-field-message" negative header={errorText}/>
            ) : (
                ""
            )}
            <div
                className={
                    "corner text-field labeled " +
                    (errorText ? "error-field " : "") +
                    (isDeclinable ? "declinable " : "")
                }
                style={style.div}
            >
                {isDeclinable && (
                    <Popup
                        className="declinable-explanation"
                        content="Cette caractéristique peut être utilisée pour créer une tête & déclinée au sein de la famille sélectionnée"
                        trigger={
                            <Label color="blue" ribbon>
                                Declinable
                            </Label>
                        }
                    />
                )}
                <Label
                    basic
                    className={"text-field-label dropdown-label " + labelClassName}
                    style={Object.assign({width: 105}, style.label)}
                >
                    {label + (isRequired ? "*" : "")}
                </Label>
                <Dropdown
                    // {...props}
                    allowAdditions={allowAdditionsIfNoOptions}
                    multiple={isMultiple}
                    onAddItem={handleAddition}
                    onLabelClick={handleLabelClick}
                    style={style.input}
                    search
                    id={code}
                    onChange={handleSelectChange}
                    renderLabel={renderLabel}
                    onOpen={onClick}
                    onClose={onClick}
                    selection
                    minCharacters={minCaracters}
                    icon={icon}
                    noResultsMessage={noResultMessage}
                    // labeled={icon !== "" ? true : false}
                    // button={icon !== "" ? true : false}
                    lazyLoad={lazyload}
                    disabled={disabled}
                    options={finalOptions}
                    loading={loading}
                    className={"dropdown dropit-dropdown " + className}
                    value={finalValue}
                />
                {formId === permanentFormIds.MULTI_MODIF && onModifyClick ? (
                    <ButtonChange
                        modified={data.modified}
                        onClick={onModifyClick}
                        code={code}
                        fieldType={fieldType}
                    />
                ) : (
                    <ButtonLock
                        hidden={!isNewProduct}
                        lockable={props.lockable}
                        onClick={onLockClick}
                        code={code}
                        lock={lock}
                        fieldType={fieldType}
                    />
                )}
            </div>
        </div>
    )
}

export default ComboBox
